import { useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Q_CREDIT_APP_PUBLIC } from '../../../../api';
import { GET_GAIG_PRODUCTS } from '../api/getGaigProducts';
import { Q_INSURANCE_QUOTE } from '../api/getInsuranceQuote';
import { Task } from '@trnsact/trnsact-shared-types';
import { GaigAvailableAsset, GaigProgramCode } from '@trnsact/aftermarket-vendor-sdk-gaig';
import { CreditApplicationResponse, GaigProductsResponse, InsuranceQuoteResponse } from '../types';
import _ from 'lodash';


export const useInsuranceQuoteDataFetch = (task: Task) => {

  const {
    data: creditApplicationData,
    loading: isCreditApplicationLoading
  } = useQuery<CreditApplicationResponse>(Q_CREDIT_APP_PUBLIC, {
    variables: { id: task.creditApplicationId },
  });

  const {
    data: gaigProductsData,
    loading: isGaigProductsDataLoading
  } = useQuery<GaigProductsResponse>(GET_GAIG_PRODUCTS, {
    variables: { accountId: task.assignedToAccountId },
  });

  const {
    data: insuranceQuoteData,
    loading: isInsuranceQuoteDataLoading
  } = useQuery<InsuranceQuoteResponse>(Q_INSURANCE_QUOTE, {
    variables: { taskId: task.taskId },
  });

  const { uniqueAssets, uniqueProgramCodes } = useMemo(() => {
    const initialAcc: { allAvailableAssets: GaigAvailableAsset[], allAvailableProgramCodes: GaigProgramCode[] } = {
      allAvailableAssets: [],
      allAvailableProgramCodes: []
    };

    const {
      allAvailableAssets,
      allAvailableProgramCodes
    } = gaigProductsData?.gaigProposalProducts?.reduce((acc: any, product: any) => {
      const programCodes = product?.aftermarketProduct?.criteria?.[0]?.event?.params?.AFTERMARKET_PRODUCT_VENDOR_API_DATA?.API_RESPONSE_DATA?.programCodes ?? [];
      const assets = programCodes.flatMap((programCode: GaigProgramCode) => programCode.availableAssets) ?? [];

      acc.allAvailableAssets.push(...assets);
      acc.allAvailableProgramCodes.push(...programCodes);

      return acc;
    }, initialAcc) ?? initialAcc;

    return {
      uniqueAssets: _.uniqBy(allAvailableAssets, 'code') as unknown as GaigAvailableAsset[],
      uniqueProgramCodes: _.uniqBy(allAvailableProgramCodes, 'ProgramCode') as unknown as GaigProgramCode[],
    }

  }, [gaigProductsData]);

  const isLoading = isCreditApplicationLoading || isGaigProductsDataLoading || isInsuranceQuoteDataLoading;

  return {
    uniqueAssets,
    uniqueProgramCodes,
    creditApplication: creditApplicationData?.creditApplicationPublic,
    insuranceQuote: insuranceQuoteData?.insuranceQuote?.insuranceQuoteByTaskId?.entity,
    isLoading,
  };
};
